import { useEffect, useRef } from 'react';

interface UseFetchGridDataParams {
  firstPageRequest: any;
  restPagesRequest: any;
  changePage: (page: number) => void;
  size: number;
  vendorId?: number;
  orderId?: string;
  currentPage?: number;
  sortBy?: any;
  countryId?: number;
  searchTerm?: string;
  showOpen?: boolean;
  showPendingCancellationEmail?: boolean;
  brandIds?: string;
  companyId?: string;
  businessUnitId?: number;
  consignmentType?: string;
  affiliateCustomerId?: number;
  commissionStatus?: string;
  consignmentIds?: string;
  affiliateCode?: string | null;
}

const useFetchGridData = ({
  firstPageRequest,
  restPagesRequest,
  changePage,
  size,
  vendorId,
  orderId,
  currentPage,
  sortBy,
  countryId,
  searchTerm,
  showOpen,
  showPendingCancellationEmail,
  brandIds,
  companyId,
  businessUnitId,
  consignmentType,
  affiliateCustomerId,
  commissionStatus,
  consignmentIds,
  affiliateCode,
}: UseFetchGridDataParams) => {
  const isInitialMount = useRef(true);
  const previousValues = useRef({
    size,
    currentPage,
    sortBy: sortBy?.field,
    sortOrder: sortBy?.defaultSort,
    countryId,
    searchTerm,
    showOpen,
    showPendingCancellationEmail,
    brandIds,
    vendorId,
    businessUnitId,
    consignmentType,
    commissionStatus,
    consignmentIds,
    affiliateCode
  });

  /* Initial load and handle all parameter changes */
  useEffect(() => {
    const hasFiltersChanged =
      previousValues.current.sortBy !== sortBy?.field ||
      previousValues.current.sortOrder !== sortBy?.defaultSort ||
      previousValues.current.countryId !== countryId ||
      previousValues.current.searchTerm !== searchTerm ||
      previousValues.current.showOpen !== showOpen ||
      previousValues.current.showPendingCancellationEmail !== showPendingCancellationEmail ||
      previousValues.current.brandIds !== brandIds ||
      previousValues.current.consignmentType !== consignmentType ||
      previousValues.current.commissionStatus !== commissionStatus ||
      previousValues.current.consignmentIds !== consignmentIds ||
      previousValues.current.affiliateCode !== affiliateCode;

    const hasSizeChanged = previousValues.current.size !== size;
    const hasPageChanged = previousValues.current.currentPage !== currentPage;
    const hasVendorChanged = previousValues.current.vendorId !== vendorId;
    const hasBusinessUnitChanged = previousValues.current.businessUnitId !== businessUnitId;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Initial mount - only call first page (skip if businessUnitId is explicitly null)
      if (businessUnitId !== null) {
        firstPageRequest.run(size, 1, 0, vendorId, orderId, sortBy?.field, sortBy?.defaultSort, searchTerm, countryId, showOpen, showPendingCancellationEmail, brandIds, companyId, businessUnitId, consignmentType, affiliateCustomerId, commissionStatus, consignmentIds, affiliateCode);
      }
    } else if (hasBusinessUnitChanged && businessUnitId) {
      // Business unit changed - reset to first page
      changePage(1);
      firstPageRequest.run(size, 1, 0, vendorId, orderId, sortBy?.field, sortBy?.defaultSort, searchTerm, countryId, showOpen, showPendingCancellationEmail, brandIds, companyId, businessUnitId, consignmentType, affiliateCustomerId, commissionStatus, consignmentIds, affiliateCode);
    } else if (hasVendorChanged && vendorId !== undefined) {
      // Vendor changed - reset to first page
      changePage(1);
      firstPageRequest.run(size, 1, 0, vendorId, orderId, sortBy?.field, sortBy?.defaultSort, searchTerm, countryId, showOpen, showPendingCancellationEmail, brandIds, companyId, businessUnitId, consignmentType, affiliateCustomerId, commissionStatus, consignmentIds, affiliateCode);
    } else if (hasFiltersChanged) {
      // Filters changed - reset to first page
      changePage(1);
      restPagesRequest.run(size, 1, 0, vendorId, orderId, sortBy?.field, sortBy?.defaultSort, searchTerm, countryId, showOpen, showPendingCancellationEmail, brandIds, companyId, businessUnitId, consignmentType, affiliateCustomerId, commissionStatus, consignmentIds, affiliateCode);
    } else if (hasSizeChanged && !hasPageChanged) {
      // Only size changed - stay on current page but recalculate offset
      const offset = size * ((currentPage || 1) - 1);
      restPagesRequest.run(size, currentPage || 1, offset, vendorId, orderId, sortBy?.field, sortBy?.defaultSort, searchTerm, countryId, showOpen, showPendingCancellationEmail, brandIds, companyId, businessUnitId, consignmentType, affiliateCustomerId, commissionStatus, consignmentIds, affiliateCode);
    } else if (hasPageChanged && currentPage && currentPage > 0) {
      // Only page changed
      changePage(currentPage);
      const offset = size * (currentPage - 1);
      restPagesRequest.run(size, currentPage, offset, vendorId, orderId, sortBy?.field, sortBy?.defaultSort, searchTerm, countryId, showOpen, showPendingCancellationEmail, brandIds, companyId, businessUnitId, consignmentType, affiliateCustomerId, commissionStatus, consignmentIds, affiliateCode);
    }

    // Update previous values
    previousValues.current = {
      size,
      currentPage,
      sortBy: sortBy?.field,
      sortOrder: sortBy?.defaultSort,
      countryId,
      searchTerm,
      showOpen,
      showPendingCancellationEmail,
      brandIds,
      vendorId,
      businessUnitId,
      consignmentType,
      commissionStatus,
      consignmentIds,
      affiliateCode
    };
    // NOTE: firstPageRequest, restPagesRequest, and changePage are expected to be stable references
    // from the parent component. If you see infinite loops, ensure these are wrapped in useCallback.
  }, [size, currentPage, sortBy, countryId, searchTerm, showOpen, showPendingCancellationEmail, brandIds, vendorId, businessUnitId, orderId, consignmentType, affiliateCustomerId, commissionStatus, consignmentIds, affiliateCode, firstPageRequest, restPagesRequest, changePage]);
};

export default useFetchGridData;
